import { Link } from "react-router-dom";
import { Wrench, Shield, Truck, Clock, CheckCircle2, Phone, MessageCircle, ArrowUpRight } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useReveal } from "../hooks/useReveal";
import { waLink } from "../lib/wa";

const SERVICES = [
  { icon: <Wrench size={24} />, t: "Annual Service Contract (ASC)", d: "Pre-paid, scheduled maintenance — every interval covered, every machine year." },
  { icon: <Shield size={24} />, t: "Extended Warranty · 5 Years", d: "Bobcat Protection Plus — extend factory warranty up to 5 years / 5,000 hrs." },
  { icon: <Truck size={24} />, t: "On-Site Service", d: "Certified technicians arrive at your jobsite with genuine parts and tooling." },
  { icon: <CheckCircle2 size={24} />, t: "One Service. One Standard.", d: "One machine, one team, for life. A single accountable point of service for every Bobcat we sell." },
  { icon: <Clock size={24} />, t: "24/7 Breakdown Support", d: "WhatsApp response within 15 minutes during working hours. Emergency line 24/7." },
];

const BENEFITS = [
  "Bobcat genuine parts only — no aftermarket substitutes",
  "Factory-trained technicians certified by Doosan Bobcat India",
  "Pan-India service support · 8 dedicated touch points across Maharashtra",
  "One Service. One Standard. One machine, one team — for life.",
  "Annual Service Contracts (ASC) for predictable, all-inclusive uptime",
  "Extended warranty plans up to 5 years / 5,000 hours",
  "Transparent pricing · written estimates before work begins",
  "Priority parts dispatch from our Panvel warehouse",
];

export default function PartsServicePage() {
  const rootRef = useReveal();
  return (
    <div ref={rootRef} className="bg-white" data-testid="parts-service-page">
      <Navbar />

      <section className="pt-36 md:pt-44 pb-16 bg-[#f5f5f7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="overline">Parts & Service</div>
            <h1 className="h1-hero text-5xl md:text-7xl mt-4">
              Uptime is our
              <br />
              <span className="text-[#ff6a13]">promise.</span>
            </h1>
            <p className="mt-6 text-lg text-[#6b6b72] max-w-xl">
              A Bobcat on a jobsite that isn't running is worse than no Bobcat at all.
              We've built our entire service operation to make sure that never happens.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={waLink({ source: "parts_service", message: "Hi Bhumi Equipments, I need a service appointment." })}
                target="_blank"
                rel="noreferrer"
                className="btn-primary"
                data-testid="service-whatsapp-btn"
              >
                <MessageCircle size={18} /> Book Service on WhatsApp
              </a>
              <a href="tel:+919167632596" className="btn-ghost" data-testid="service-call-btn">
                <Phone size={16} /> Call +91 91676 32596
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 reveal">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[#0a0a0c] relative">
              <img
                src="https://images.unsplash.com/photo-1581094651181-35942459ef62?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Bobcat service"
                className="absolute inset-0 w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <div className="overline text-[#ff6a13]">Service Centre</div>
                <div className="mt-2 font-display text-2xl font-bold">Panvel, Maharashtra</div>
                <div className="mt-1 text-sm text-white/70">Pan-state mobile service network</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="reveal max-w-2xl">
            <div className="overline">What we do</div>
            <h2 className="h2-section text-4xl md:text-5xl mt-4">
              A full stack of services, one number to call.
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {SERVICES.map((s) => (
              <div
                key={s.t}
                className="reveal p-8 rounded-3xl bg-[#f5f5f7] hover:bg-white border border-transparent hover:border-[#ff6a13]/40 transition-all"
                data-testid={`service-${s.t.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="text-[#ff6a13]">{s.icon}</div>
                <div className="mt-5 font-display text-xl font-bold text-[#111113]">{s.t}</div>
                <p className="mt-2 text-sm text-[#6b6b72] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Genuine parts */}
      <section className="py-24 bg-[#0a0a0c] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <div className="overline text-[#ff6a13]">Genuine Parts</div>
            <h3 className="h2-section text-3xl md:text-5xl mt-4">
              If it isn't Bobcat-genuine,
              <br />
              we won't put it in your machine.
            </h3>
            <p className="mt-6 text-white/60 text-lg max-w-xl">
              Every filter, belt, hose, hydraulic line and electronic we stock is
              Bobcat OEM — sourced directly from Doosan Bobcat India's Chennai hub
              and warehoused in Panvel for same-day dispatch across the state.
            </p>
          </div>
          <div className="reveal space-y-3">
            {BENEFITS.map((b) => (
              <div key={b} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-4">
                <CheckCircle2 className="text-[#ff6a13] shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-white/80">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center reveal">
          <div className="overline">Service My Machine</div>
          <h3 className="h2-section text-4xl md:text-6xl mt-4">
            Give us the model.
            <br />
            <span className="text-[#6b6b72]">We'll send a technician.</span>
          </h3>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/book-demo" className="btn-primary">
              Schedule Service <ArrowUpRight size={16} />
            </Link>
            <Link to="/models" className="btn-ghost">
              Browse Models
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
